export type HudPromptTone = 'neutral' | 'water' | 'food' | 'danger';

export interface HudGameState {
  health: number;
  maxHealth: number;
  water: number;
  maxWater: number;
  harmony: number;
  harmonyTarget: number;
  objective: string;
  questStage: number;
  questCount: number;
  prompt: string | null;
  promptKey: string;
  promptTone: HudPromptTone;
  heldItem: string | null;
  paused: boolean;
  collapsed: boolean;
  complete: boolean;
  loading: boolean;
  introVisible: boolean;
  heatWarning: boolean;
}

export interface HudActions {
  onPause?: () => void;
  onResume?: () => void;
  onRestart?: () => void;
  onContinue?: () => void;
  onIntroDismiss?: () => void;
  onTouchAction?: (action: 'interact' | 'primary', active: boolean) => void;
}

export interface HudTouchElements {
  stick: HTMLElement;
  knob: HTMLElement;
  primaryButton: HTMLButtonElement;
  interactButton: HTMLButtonElement;
}

type HudActionName =
  | 'pause'
  | 'resume'
  | 'restart'
  | 'continue'
  | 'intro-dismiss'
  | 'interact'
  | 'primary';

interface HudActionDetail {
  action: HudActionName;
  active?: boolean;
}

const DEFAULT_STATE: HudGameState = {
  health: 100,
  maxHealth: 100,
  water: 0,
  maxWater: 100,
  harmony: 0,
  harmonyTarget: 3,
  objective: 'Revive the dry saplings',
  questStage: 1,
  questCount: 3,
  prompt: null,
  promptKey: 'E',
  promptTone: 'neutral',
  heldItem: null,
  paused: false,
  collapsed: false,
  complete: false,
  loading: false,
  introVisible: true,
  heatWarning: false,
};

export class Hud {
  private readonly healthValue = this.getElement<HTMLOutputElement>('#health-value');
  private readonly healthMeter = this.getElement<HTMLElement>('#health-meter');
  private readonly healthNote = this.getElement<HTMLElement>('#health-note');
  private readonly vitalCluster = this.getElement<HTMLElement>('.vital-cluster');
  private readonly waterValue = this.getElement<HTMLOutputElement>('#water-value');
  private readonly waterMeter = this.getElement<HTMLElement>('#water-meter');
  private readonly waterNote = this.getElement<HTMLElement>('#water-note');
  private readonly waterCluster = this.getElement<HTMLElement>('.water-cluster');
  private readonly harmonyValue = this.getElement<HTMLOutputElement>('#harmony-value');
  private readonly harmonyTarget = this.getElement<HTMLOutputElement>('#harmony-target');
  private readonly harmonyPips = this.getElement<HTMLElement>('#harmony-pips');
  private readonly harmonyCluster = this.getElement<HTMLElement>('.harmony-cluster');
  private readonly questStage = this.getElement<HTMLElement>('#quest-stage');
  private readonly objectiveLabel = this.getElement<HTMLElement>('#objective-label');
  private readonly prompt = this.getElement<HTMLElement>('#interaction-prompt');
  private readonly promptKey = this.getElement<HTMLElement>('#prompt-key');
  private readonly promptLabel = this.getElement<HTMLElement>('#prompt-label');
  private readonly heldItem = this.getElement<HTMLElement>('#held-item');
  private readonly heldItemLabel = this.getElement<HTMLElement>('#held-item-label');
  private readonly primaryActionLabel = this.getElement<HTMLElement>('#primary-action-label');
  private readonly journeyOverlay = this.getElement<HTMLElement>('#journey-overlay');
  private readonly journeyMessage = this.getElement<HTMLElement>('#journey-message');
  private readonly beginButton = this.getElement<HTMLButtonElement>('#begin-button');
  private readonly pauseOverlay = this.getElement<HTMLElement>('#pause-overlay');
  private readonly collapseOverlay = this.getElement<HTMLElement>('#collapse-overlay');
  private readonly completionOverlay = this.getElement<HTMLElement>('#completion-overlay');
  private readonly screenWash = this.getElement<HTMLElement>('#screen-wash');
  private readonly scoreValue = this.getElement<HTMLElement>('#score-value');
  private readonly targetValue = this.getElement<HTMLElement>('#target-value');
  private readonly timerValue = this.getElement<HTMLElement>('#timer-value');
  private readonly touchElements: HudTouchElements = {
    stick: this.getElement<HTMLElement>('#touch-stick'),
    knob: this.getElement<HTMLElement>('#touch-knob'),
    primaryButton: this.getElement<HTMLButtonElement>('#dash-button'),
    interactButton: this.getElement<HTMLButtonElement>('#interact-button'),
  };

  private state: HudGameState = { ...DEFAULT_STATE };
  private actions: HudActions = {};
  private renderedHarmonyTarget = -1;
  private feedbackTimer: number | undefined;

  private readonly onUiClick = (event: Event): void => {
    const button = (event.target as Element | null)?.closest<HTMLButtonElement>('button');
    if (!button) return;

    switch (button.id) {
      case 'pause-button':
        if (this.actions.onPause) this.actions.onPause();
        else this.setPaused(true);
        this.emitAction('pause');
        break;
      case 'resume-button':
        if (this.actions.onResume) this.actions.onResume();
        else this.setPaused(false);
        this.emitAction('resume');
        break;
      case 'pause-restart-button':
      case 'rest-button':
      case 'completion-restart-button':
        this.actions.onRestart?.();
        this.emitAction('restart');
        break;
      case 'wander-button':
        if (this.actions.onContinue) this.actions.onContinue();
        else this.showCompletion(false);
        this.emitAction('continue');
        break;
      case 'begin-button':
        if (this.state.loading) return;
        this.showIntro(false);
        this.actions.onIntroDismiss?.();
        this.emitAction('intro-dismiss');
        break;
    }
  };

  private readonly onTouchActionDown = (event: PointerEvent): void => {
    const button = event.currentTarget as HTMLButtonElement;
    event.preventDefault();
    button.classList.add('is-active');
    try {
      button.setPointerCapture(event.pointerId);
    } catch {
      // Synthetic pointer events may not expose a capturable pointer id.
    }
    this.dispatchTouchAction(button, true);
  };

  private readonly onTouchActionUp = (event: PointerEvent): void => {
    const button = event.currentTarget as HTMLButtonElement;
    event.preventDefault();
    button.classList.remove('is-active');
    this.dispatchTouchAction(button, false);
  };

  private readonly onInitialInput = (event: KeyboardEvent | PointerEvent): void => {
    if (!this.state.introVisible || this.state.loading) return;
    if (event instanceof PointerEvent && (event.target as Element | null)?.closest('#begin-button')) {
      return;
    }
    if (
      event instanceof KeyboardEvent &&
      !['KeyW', 'KeyA', 'KeyS', 'KeyD', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'KeyE', 'KeyF'].includes(
        event.code,
      )
    ) {
      return;
    }
    this.showIntro(false);
  };

  constructor() {
    document.querySelector('#app')?.addEventListener('click', this.onUiClick);
    window.addEventListener('keydown', this.onInitialInput);
    window.addEventListener('pointerdown', this.onInitialInput);
    for (const button of [this.touchElements.primaryButton, this.touchElements.interactButton]) {
      button.addEventListener('pointerdown', this.onTouchActionDown);
      button.addEventListener('pointerup', this.onTouchActionUp);
      button.addEventListener('pointercancel', this.onTouchActionUp);
      button.addEventListener('lostpointercapture', this.onTouchActionUp);
    }
    this.render();
  }

  updateGameState(state: Partial<HudGameState>): void {
    this.state = { ...this.state, ...state };
    this.render();
  }

  setPrompt(
    label: string | null,
    key = 'E',
    tone: HudPromptTone = 'neutral',
  ): void {
    this.updateGameState({ prompt: label, promptKey: key, promptTone: tone });
  }

  setPaused(paused: boolean): void {
    this.updateGameState({ paused });
  }

  showCollapse(visible = true): void {
    this.updateGameState({ collapsed: visible, paused: false });
  }

  showCompletion(visible = true): void {
    this.updateGameState({ complete: visible, paused: false });
  }

  showLoading(visible = true, message = 'Reading the wind and waking the savanna...'): void {
    this.journeyMessage.textContent = message;
    this.updateGameState({ loading: visible, introVisible: visible || this.state.introVisible });
  }

  showIntro(visible = true): void {
    this.updateGameState({ introVisible: visible, loading: false });
  }

  setTouchActionLabel(label: string): void {
    this.primaryActionLabel.textContent = label;
  }

  bindActions(actions: HudActions): void {
    this.actions = { ...actions };
  }

  getTouchElements(): HudTouchElements {
    return this.touchElements;
  }

  flashWater(kind: 'fill' | 'spray' | 'empty'): void {
    const className = kind === 'fill' ? 'is-filling' : kind === 'spray' ? 'is-spraying' : 'is-empty';
    this.restartClassAnimation(this.waterCluster, className, 720);
    this.flashScreen(kind === 'empty' ? 'danger' : 'water');
  }

  flashHealth(kind: 'restore' | 'damage'): void {
    this.flashScreen(kind === 'restore' ? 'health' : 'danger');
  }

  flashPickup(label = 'Gathered with care'): void {
    this.setTransientFeedback(label, 'food');
    this.flashScreen('health');
  }

  flashQuest(): void {
    this.restartClassAnimation(this.harmonyCluster, 'is-advancing', 760);
  }

  setTarget(target: number): void {
    this.updateGameState({ harmonyTarget: target });
  }

  update(score: number, target: number, elapsed: number, complete: boolean): void {
    const minutes = Math.floor(elapsed / 60).toString().padStart(2, '0');
    const seconds = Math.floor(elapsed % 60).toString().padStart(2, '0');
    this.scoreValue.textContent = String(score);
    this.targetValue.textContent = String(target);
    this.timerValue.textContent = `${minutes}:${seconds}`;
    this.updateGameState({ harmony: score, harmonyTarget: target, complete });
  }

  dispose(): void {
    document.querySelector('#app')?.removeEventListener('click', this.onUiClick);
    window.removeEventListener('keydown', this.onInitialInput);
    window.removeEventListener('pointerdown', this.onInitialInput);
    for (const button of [this.touchElements.primaryButton, this.touchElements.interactButton]) {
      button.removeEventListener('pointerdown', this.onTouchActionDown);
      button.removeEventListener('pointerup', this.onTouchActionUp);
      button.removeEventListener('pointercancel', this.onTouchActionUp);
      button.removeEventListener('lostpointercapture', this.onTouchActionUp);
    }
    if (this.feedbackTimer !== undefined) window.clearTimeout(this.feedbackTimer);
  }

  private render(): void {
    const health = this.clamp(this.state.health, 0, Math.max(this.state.maxHealth, 1));
    const water = this.clamp(this.state.water, 0, Math.max(this.state.maxWater, 1));
    const harmony = Math.floor(this.clamp(this.state.harmony, 0, Math.max(this.state.harmonyTarget, 0)));
    const healthPercent = (health / Math.max(this.state.maxHealth, 1)) * 100;
    const waterPercent = (water / Math.max(this.state.maxWater, 1)) * 100;

    this.setMeter(this.healthMeter, health, this.state.maxHealth, healthPercent);
    this.setMeter(this.waterMeter, water, this.state.maxWater, waterPercent);
    this.healthValue.textContent = String(Math.round(health));
    this.waterValue.textContent = String(Math.round(water));
    this.healthNote.textContent = this.healthDescription(healthPercent);
    this.waterNote.textContent = this.waterDescription(waterPercent);
    this.vitalCluster.classList.toggle('is-warning', healthPercent <= 30 || this.state.heatWarning);

    this.harmonyValue.textContent = String(harmony);
    this.harmonyTarget.textContent = String(this.state.harmonyTarget);
    this.questStage.textContent = `Quest ${this.state.questStage} of ${this.state.questCount}`;
    this.objectiveLabel.textContent = this.state.objective;
    this.renderHarmony(harmony, this.state.harmonyTarget, this.state.objective);

    const hasPrompt = Boolean(this.state.prompt);
    this.prompt.classList.toggle('is-visible', hasPrompt);
    this.prompt.setAttribute('aria-hidden', String(!hasPrompt));
    this.prompt.dataset.tone = this.state.promptTone;
    this.promptLabel.textContent = this.state.prompt ?? '';
    this.promptKey.textContent = this.state.promptKey;

    const hasHeldItem = Boolean(this.state.heldItem);
    this.heldItem.classList.toggle('is-visible', hasHeldItem);
    this.heldItem.setAttribute('aria-hidden', String(!hasHeldItem));
    this.heldItemLabel.textContent = this.state.heldItem ?? 'Nothing';
    this.primaryActionLabel.textContent = hasHeldItem ? 'Throw' : water > 0 ? 'Spray' : 'Trunk';

    this.setOverlay(this.pauseOverlay, this.state.paused && !this.state.collapsed && !this.state.complete);
    this.setOverlay(this.collapseOverlay, this.state.collapsed);
    this.setOverlay(this.completionOverlay, this.state.complete && !this.state.collapsed);
    this.setOverlay(this.journeyOverlay, this.state.loading || this.state.introVisible);
    this.journeyOverlay.classList.toggle('is-loading', this.state.loading);
    this.beginButton.disabled = this.state.loading;
    this.beginButton.textContent = this.state.loading ? 'Waking the savanna...' : 'Begin the journey ->';
  }

  private renderHarmony(value: number, target: number, objective: string): void {
    const safeTarget = Math.max(0, Math.floor(target));
    if (safeTarget !== this.renderedHarmonyTarget) {
      const fragment = document.createDocumentFragment();
      for (let index = 0; index < safeTarget; index += 1) {
        const pip = document.createElement('span');
        pip.className = 'harmony-pip';
        pip.setAttribute('aria-hidden', 'true');
        fragment.append(pip);
      }
      this.harmonyPips.replaceChildren(fragment);
      this.renderedHarmonyTarget = safeTarget;
    }

    const pips = this.harmonyPips.querySelectorAll<HTMLElement>('.harmony-pip');
    pips.forEach((pip, index) => pip.classList.toggle('is-restored', index < value));
    this.harmonyPips.setAttribute('aria-label', `${objective}: ${value} of ${safeTarget}`);
  }

  private setMeter(element: HTMLElement, value: number, max: number, percent: number): void {
    element.style.setProperty('--meter-progress', `${percent.toFixed(2)}%`);
    element.setAttribute('aria-valuemax', String(max));
    element.setAttribute('aria-valuenow', String(Math.round(value)));
  }

  private setOverlay(element: HTMLElement, visible: boolean): void {
    element.classList.toggle('is-visible', visible);
    element.setAttribute('aria-hidden', String(!visible));
  }

  private dispatchTouchAction(button: HTMLButtonElement, active: boolean): void {
    const action = button === this.touchElements.interactButton ? 'interact' : 'primary';
    this.actions.onTouchAction?.(action, active);
    this.emitAction(action, active);
  }

  private emitAction(action: HudActionName, active?: boolean): void {
    window.dispatchEvent(
      new CustomEvent<HudActionDetail>('elephant:ui-action', { detail: { action, active } }),
    );
  }

  private setTransientFeedback(label: string, tone: HudPromptTone): void {
    if (this.feedbackTimer !== undefined) window.clearTimeout(this.feedbackTimer);
    const previous = {
      prompt: this.state.prompt,
      promptKey: this.state.promptKey,
      promptTone: this.state.promptTone,
    };
    this.updateGameState({ prompt: label, promptKey: '+', promptTone: tone });
    this.feedbackTimer = window.setTimeout(() => {
      this.updateGameState(previous);
      this.feedbackTimer = undefined;
    }, 1050);
  }

  private flashScreen(kind: 'water' | 'health' | 'danger'): void {
    this.restartClassAnimation(this.screenWash, `is-${kind}`, 520);
  }

  private restartClassAnimation(element: HTMLElement, className: string, duration: number): void {
    element.classList.remove(className);
    void element.offsetWidth;
    element.classList.add(className);
    window.setTimeout(() => element.classList.remove(className), duration);
  }

  private healthDescription(percent: number): string {
    if (percent <= 25) return 'Find shade, water, or fruit';
    if (percent <= 55) return 'The midday heat is heavy';
    if (percent <= 80) return 'Steady on the trail';
    return 'Strong as the old baobab';
  }

  private waterDescription(percent: number): string {
    if (percent <= 0) return 'Find the waterhole';
    if (percent < 35) return 'A little water remains';
    if (percent < 85) return 'Ready to spray';
    return 'Trunk full and cool';
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(Number.isFinite(value) ? value : min, min), max);
  }

  private getElement<T extends HTMLElement>(selector: string): T {
    const element = document.querySelector<T>(selector);
    if (!element) throw new Error(`Missing HUD element: ${selector}`);
    return element;
  }
}
